import * as React from 'react'
import { RxExternalLink } from '@react-icons/all-files/rx/RxExternalLink'
import { Link } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { TRACK } from '@disco/constants/ItemTypes'
import {
  fetchResults,
  selectSearchPage,
  setFilters,
  setQuery,
  setSearchType,
} from '@disco/state-management/reducers/search-page'

import { DiscoCallout, Text } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import styles from './index.module.css'

type Props = {
  // True when the containing pane is on a Tracks *view* (Browse/group panes,
  // which use pane.view rather than the search `type`).
  isTrackView?: boolean
}

/**
 * Banner over the search results linking to the full /search table. Hands the
 * current Browse search — query, badge filters and mode — to the search page.
 * Both stores keep filters as FilterState[], so they copy across without
 * translation.
 */
const OpenInSearchTableBanner = ({
  isTrackView = false,
}: Props): React.ReactElement | null => {
  const dispatch = useDispatch()
  const searchTerm = useSelector(state => state.search.searchTerm)
  const filterState = useSelector(state => state.search.filterState)
  const searchMode = useSelector(state => state.search.searchMode)
  const type = useSelector(state => state.search.type)
  const initialFetchLoaded = useSelector(
    state => selectSearchPage(state).initialFetchLoaded,
  )

  // The /search table is a track-first view, so only surface the link on the
  // Tracks results — either the search Tracks tab (search `type`) or a
  // Browse/group Tracks view (isTrackView).
  if (!isTrackView && type !== TRACK) return null

  const handleClick = () => {
    dispatch(setSearchType(searchMode ?? 'keyword'))
    dispatch(setQuery(searchTerm ?? ''))
    dispatch(setFilters(filterState ?? []))
    // The search page auto-fetches on its first mount; a return visit (already
    // loaded once) needs the fetch dispatched here — same as the search banners.
    if (initialFetchLoaded) dispatch(fetchResults())
    mixpanel.sendPureEvent('Click Link', {
      Context: 'Search',
      Type: 'Open in search table',
      Location: 'Browse',
    })
  }

  return (
    <DiscoCallout type="feature" size="1" mx="3" mb="2">
      <Link to="/search" onClick={handleClick} className={styles.link}>
        <Text size="1" weight="medium">
          View in table format
        </Text>
        <RxExternalLink size={13} aria-hidden />
      </Link>
    </DiscoCallout>
  )
}

export default OpenInSearchTableBanner
